import { useMemo, useEffect, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { RefreshCw, AlertCircle, ExternalLink } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useAppStore } from '@/stores/app-store';
import { useSettingsStore } from '@/stores/settings-store';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { EDITOR_THEMES } from '@/config/themes';
import type { MarkdownTab } from '@/types';

/**
 * In-app markdown reader for `docs/` and `manual/` files. Renders via
 * react-markdown + remark-gfm; code blocks highlight through
 * rehype-highlight (highlight.js). The hljs token classes are themed by
 * a small CSS layer driven from the user's selected EDITOR_THEMES entry,
 * so code in docs looks the same as code in the SQL editor.
 *
 * The reader is the only consumer of `/api/docs/file` and follows the
 * server's strict allowlist — no path the user types in the URL bar can
 * leak files outside `docs/` and `manual/`.
 */

interface MarkdownViewProps {
  tab: MarkdownTab;
}

interface DocResponse {
  path: string;
  scope: 'docs' | 'manual';
  content: string;
  mtime: string;
}

export function MarkdownView({ tab }: MarkdownViewProps) {
  const appTheme = useAppStore((s) => s.theme);
  const fontSize = useSettingsStore((s) => s.getNumber('markdown.fontSize', 13));
  const fontFamily = useSettingsStore((s) =>
    s.get('markdown.fontFamily', 'system-ui, -apple-system, sans-serif'));
  const lineHeight = useSettingsStore((s) => s.getNumber('markdown.lineHeight', 1.65));
  const maxWidth = useSettingsStore((s) => s.getNumber('markdown.maxWidth', 720));
  const codeFontSize = useSettingsStore((s) => s.getNumber('markdown.codeFontSize', 12));
  const codeFontFamily = useSettingsStore((s) =>
    s.get('markdown.codeFontFamily', "'JetBrains Mono', monospace"));
  const codeThemeId = useSettingsStore((s) =>
    s.get(appTheme === 'dark' ? 'markdown.codeTheme.dark' : 'markdown.codeTheme.light',
          appTheme === 'dark' ? 'dbgo-dark' : 'dbgo-light'));

  const codeTheme = useMemo(
    () => EDITOR_THEMES.find((t) => t.id === codeThemeId) ?? EDITOR_THEMES[0],
    [codeThemeId],
  );

  const { data, isLoading, error, refetch } = useQuery<DocResponse>({
    queryKey: ['docs', tab.path],
    queryFn: () => apiClient.get<DocResponse>(`/api/docs/file?path=${encodeURIComponent(tab.path)}`),
    staleTime: 60_000,
  });

  // Build the highlight.js CSS once per theme change. Highlight.js writes
  // class names like `.hljs-keyword`, `.hljs-string`, etc. We map the
  // editor theme's Monaco token rules onto those classes so code blocks
  // in docs match the SQL editor visually.
  const codeStyles = useMemo(() => buildHljsCss(codeTheme), [codeTheme]);

  // Intercept link clicks for `*.md` relative paths inside the same
  // scope — open them in a new MarkdownView tab instead of navigating.
  // External `https://` links open normally in a new browser tab.
  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const a = (e.target as HTMLElement).closest('a');
      if (!a) return;
      const href = a.getAttribute('href') ?? '';

      // Same-page anchor — let the browser handle smooth-scroll.
      if (href.startsWith('#')) return;

      // External URL — let it open normally in a new tab. Markdown
      // renderer already adds rel=noopener.
      if (/^https?:\/\//i.test(href) || href.startsWith('//')) return;

      e.preventDefault();

      // Resolve relative path against the current doc's directory.
      // SAFETY: we restrict to same scope (no `../manual/x.md` from a
      // doc in `docs/`), matching the server's allowlist.
      const currentDir = tab.path.split('/').slice(0, -1).join('/');
      let target = href;
      if (target.startsWith('./')) target = target.slice(2);
      // Same-folder shorthand: `other-doc.md` → `<currentDir>/other-doc.md`
      if (!target.includes('/')) target = `${currentDir}/${target}`;

      // Reject anything that escapes the current scope's folder.
      if (!target.startsWith(`${currentDir}/`) || target.includes('..')) {
        return;
      }

      const filename = target.split('/').pop() ?? target;
      const title = filename.replace(/\.md$/, '').replace(/[-_]/g, ' ');
      useWorkspaceStore.getState().openTab({
        type: 'markdown',
        id: `markdown:${target}`,
        path: target,
        title: title.charAt(0).toUpperCase() + title.slice(1),
      });
    },
    [tab.path],
  );

  // Track whether the rendered content contains an h1 we should hoist
  // into the tab title — saves the user from "api-keys" headers when
  // the doc itself says "API Keys".
  const updateTab = useWorkspaceStore((s) => s.updateTab);
  useEffect(() => {
    if (!data?.content) return;
    const match = /^#\s+(.+)$/m.exec(data.content);
    if (match && match[1] !== tab.title) {
      updateTab(tab.id, { title: match[1].trim() });
    }
  }, [data?.content, tab.id, tab.title, updateTab]);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center text-text-muted text-xs">
        Loading {tab.path}…
      </div>
    );
  }
  if (error) {
    const msg = error instanceof Error ? error.message : 'Unknown error';
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex items-center gap-2 px-4 py-2 bg-accent-red/10 border border-accent-red/30 rounded-sm text-xs text-accent-red">
          <AlertCircle size={14} />
          <span>Failed to load {tab.path}: {msg}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-dbgo-workarea">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 h-[38px] bg-dbgo-panel border-b flex-shrink-0">
        <span className="text-2xs text-text-muted font-mono truncate">{tab.path}</span>
        {data?.mtime && (
          <span className="text-2xs text-text-muted">
            · updated {new Date(data.mtime).toLocaleDateString()}
          </span>
        )}
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => refetch()}
          className="w-6 h-6 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors"
          title="Reload"
        >
          <RefreshCw size={13} />
        </button>
      </div>

      {/* Inject highlight.js theme styles inline so the markdown view is
          self-contained. They scope to the .markdown-prose container so
          they don't leak into other components. */}
      <style>{codeStyles}</style>

      {/* Body */}
      <div
        className="flex-1 overflow-y-auto markdown-prose"
        style={{
          fontFamily,
          fontSize: `${fontSize}px`,
          lineHeight,
        }}
        onClick={handleLinkClick}
      >
        <div
          className="mx-auto px-6 py-6"
          style={{ maxWidth: `${maxWidth}px` }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[[rehypeHighlight, { detect: true, ignoreMissing: true }]]}
            components={{
              a({ href, children, ...props }) {
                const isExternal = /^https?:\/\//.test(href ?? '');
                return (
                  <a
                    href={href}
                    target={isExternal ? '_blank' : undefined}
                    rel={isExternal ? 'noopener noreferrer' : undefined}
                    {...props}
                  >
                    {children}
                    {isExternal && <ExternalLink size={11} className="inline-block ml-0.5 -mt-px opacity-60" />}
                  </a>
                );
              },
              code({ className, children, ...rest }) {
                const isInline = !className?.startsWith('language-');
                return (
                  <code
                    className={className}
                    style={
                      isInline
                        ? { fontFamily: codeFontFamily, fontSize: `${codeFontSize}px` }
                        : { fontFamily: codeFontFamily, fontSize: `${codeFontSize}px`, lineHeight: 1.5 }
                    }
                    {...rest}
                  >
                    {children}
                  </code>
                );
              },
            }}
          >
            {data?.content ?? ''}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
}

// ─── Theming ───────────────────────────────────────────────────────────
//
// `rehype-highlight` decorates code blocks with classes like `.hljs-
// keyword`, `.hljs-string`, etc. Our editor themes already define rich
// palettes for the same concepts (Monaco token rules: keyword, string,
// number, comment, type, predefined, operator). Map both onto each
// other and the user's "Markdown code theme" choice picks an entire
// editor theme as the Markdown highlighter palette.

interface RuleMap { [token: string]: string }

function rulesToMap(theme: typeof EDITOR_THEMES[number]): RuleMap {
  const out: RuleMap = {};
  for (const r of theme.monaco.rules) {
    if (typeof r.foreground === 'string') {
      out[r.token] = `#${r.foreground}`;
    }
  }
  return out;
}

function buildHljsCss(theme: typeof EDITOR_THEMES[number]): string {
  const r = rulesToMap(theme);
  const bg = (theme.monaco.colors['editor.background'] as string | undefined) ?? '#12141a';
  const fg = (theme.monaco.colors['editor.foreground'] as string | undefined) ?? '#e1e4eb';

  // Defaults pull from the theme's foreground when a token isn't
  // explicitly defined — gives every theme passable coverage even
  // when its rule list is shallow.
  const keyword = r.keyword ?? fg;
  const str = r.string ?? '#98c379';
  const num = r.number ?? '#d19a66';
  const comment = r.comment ?? '#5c6370';
  const type = r.type ?? '#e5c07b';
  const predefined = r.predefined ?? '#61afef';
  const operator = r.operator ?? fg;

  return `
.markdown-prose pre {
  background: ${bg};
  color: ${fg};
}
.markdown-prose .hljs-comment,
.markdown-prose .hljs-quote { color: ${comment}; font-style: italic; }
.markdown-prose .hljs-keyword,
.markdown-prose .hljs-selector-tag,
.markdown-prose .hljs-meta-keyword { color: ${keyword}; }
.markdown-prose .hljs-string,
.markdown-prose .hljs-template-string,
.markdown-prose .hljs-attr { color: ${str}; }
.markdown-prose .hljs-number,
.markdown-prose .hljs-literal { color: ${num}; }
.markdown-prose .hljs-type,
.markdown-prose .hljs-class .hljs-title,
.markdown-prose .hljs-title.class_ { color: ${type}; }
.markdown-prose .hljs-built_in,
.markdown-prose .hljs-variable.language_,
.markdown-prose .hljs-name { color: ${predefined}; }
.markdown-prose .hljs-operator,
.markdown-prose .hljs-symbol,
.markdown-prose .hljs-punctuation { color: ${operator}; }
.markdown-prose .hljs-section,
.markdown-prose .hljs-title { color: ${type}; font-weight: 600; }
.markdown-prose .hljs-emphasis { font-style: italic; }
.markdown-prose .hljs-strong { font-weight: 700; }
`;
}
